import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PROFESSIONS, MEDICAL_SPECIALTIES, CONTRACT_TYPES, CLIENT_TYPES, PLACEMENT_TYPES, FACILITY_TYPES, SOURCING_SCOPES, WORK_RIGHTS_OPTIONS } from "../../data/site";
import { Field, GdprConsent, FormSuccess, inputCls } from "../forms/FormBits";
import StepBar from "./StepBar";

const STEPS = ["Organisation", "Vacancy", "Clinical & Compliance"];

const initial = {
  company_name: "", contact_person: "", contact_email: "", contact_phone: "",
  industry: "Healthcare", location: "", client_type: CLIENT_TYPES[0],
  job_title: "", profession: PROFESSIONS[0], medical_specialty: MEDICAL_SPECIALTIES[0],
  number_of_vacancies: "1", job_description: "", required_skills: "",
  placement_type: PLACEMENT_TYPES[0], sourcing_scope: SOURCING_SCOPES[0], facility_type: FACILITY_TYPES[0],
  salary: "", contract_type: CONTRACT_TYPES[0], working_hours: "", shift_pattern: "", start_date: "",
  certifications_required: "", work_rights_required: [],
  required_registration: "", required_documents: "", visa_requirements: "", country_regulations: "",
  gdpr_consent: false,
};

export const JobRequest = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (form.company_name.trim().length < 2) e.company_name = "Enter your organisation name.";
      if (form.contact_person.trim().length < 2) e.contact_person = "Enter a contact name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) e.contact_email = "Enter a valid email.";
    }
    if (step === 1 && form.job_title.trim().length < 2) e.job_title = "Enter the job title.";
    if (step === 2 && !form.gdpr_consent) e.gdpr_consent = "GDPR consent is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        required_skills: form.required_skills.split(",").map((s) => s.trim()).filter(Boolean),
        certifications_required: form.certifications_required.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/intake/job", payload);
      setDone(true);
      toast.success("Talent request submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <FormSuccess
        title="Your talent request has been logged"
        message="Your requirement has been added to our Job Order Intelligence Database. A consultant will contact you to confirm details and begin the recruitment process."
        onReset={() => { setForm(initial); setStep(0); setDone(false); }}
        resetLabel="Submit another request"
      />
    );

  return (
    <div data-testid="job-request">
      <StepBar steps={STEPS} current={step} />

      {step === 0 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Organisation name" required error={errors.company_name}><input className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="job-company" /></Field>
          <Field label="Contact person" required error={errors.contact_person}><input className={inputCls} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="job-contact" /></Field>
          <Field label="Email" required error={errors.contact_email}><input type="email" className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="job-email" /></Field>
          <Field label="Phone"><input className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="job-phone" /></Field>
          <Field label="Location"><input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="job-location" /></Field>
          <Field label="Client type">
            <select className={inputCls} value={form.client_type} onChange={(e) => set("client_type", e.target.value)} data-testid="job-clienttype">{CLIENT_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Job title" required error={errors.job_title}><input className={inputCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="job-title" /></Field>
          <Field label="Profession">
            <select className={inputCls} value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="job-profession">{PROFESSIONS.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Medical specialty">
            <select className={inputCls} value={form.medical_specialty} onChange={(e) => set("medical_specialty", e.target.value)} data-testid="job-specialty">{MEDICAL_SPECIALTIES.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Number of vacancies"><input className={inputCls} value={form.number_of_vacancies} onChange={(e) => set("number_of_vacancies", e.target.value)} data-testid="job-vacancies" /></Field>
          <Field label="Placement type">
            <select className={inputCls} value={form.placement_type} onChange={(e) => set("placement_type", e.target.value)} data-testid="job-placement-type">{PLACEMENT_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Sourcing scope">
            <select className={inputCls} value={form.sourcing_scope} onChange={(e) => set("sourcing_scope", e.target.value)} data-testid="job-sourcing-scope">{SOURCING_SCOPES.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Job description"><textarea rows={3} className={inputCls} value={form.job_description} onChange={(e) => set("job_description", e.target.value)} data-testid="job-description" /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Required skills (comma separated)"><input className={inputCls} value={form.required_skills} onChange={(e) => set("required_skills", e.target.value)} data-testid="job-skills" /></Field>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-background p-4">
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-charcoal/60">Clinical Depth &amp; Facility Fit <span className="font-medium normal-case text-muted-foreground">(optional — for healthcare roles)</span></p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Facility fit">
                <select className={inputCls} value={form.facility_type} onChange={(e) => set("facility_type", e.target.value)} data-testid="job-facility-type">{FACILITY_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
              </Field>
              <Field label="Required certifications (comma separated)"><input className={inputCls} value={form.certifications_required} onChange={(e) => set("certifications_required", e.target.value)} placeholder="e.g. ACLS, BLS, PALS" data-testid="job-certifications" /></Field>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold text-charcoal/70">Work rights required</p>
              <div className="flex flex-wrap gap-2" data-testid="job-work-rights">
                {WORK_RIGHTS_OPTIONS.map((w) => {
                  const active = form.work_rights_required.includes(w);
                  return (
                    <button
                      type="button"
                      key={w}
                      onClick={() => set("work_rights_required", active ? form.work_rights_required.filter((x) => x !== w) : [...form.work_rights_required, w])}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active ? "border-navy bg-navy text-white" : "border-border bg-white text-charcoal hover:border-navy"}`}
                      data-testid={`job-work-right-${w.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
                    >
                      {w}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Salary / rate"><input className={inputCls} value={form.salary} onChange={(e) => set("salary", e.target.value)} data-testid="job-salary" /></Field>
            <Field label="Contract type">
              <select className={inputCls} value={form.contract_type} onChange={(e) => set("contract_type", e.target.value)} data-testid="job-contract">{CONTRACT_TYPES.map((o) => <option key={o}>{o}</option>)}</select>
            </Field>
            <Field label="Working hours"><input className={inputCls} value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} placeholder="e.g. 39 hrs/week" data-testid="job-hours" /></Field>
            <Field label="Shift pattern"><input className={inputCls} value={form.shift_pattern} onChange={(e) => set("shift_pattern", e.target.value)} placeholder="e.g. Days / Nights / Rotating" data-testid="job-shift" /></Field>
            <Field label="Start date"><input className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} placeholder="e.g. ASAP / Q3 2026" data-testid="job-start" /></Field>
            <Field label="Required registration"><input className={inputCls} value={form.required_registration} onChange={(e) => set("required_registration", e.target.value)} placeholder="e.g. NMBI / NMC" data-testid="job-registration" /></Field>
            <Field label="Required documents"><input className={inputCls} value={form.required_documents} onChange={(e) => set("required_documents", e.target.value)} data-testid="job-documents" /></Field>
            <Field label="Visa / sponsorship"><input className={inputCls} value={form.visa_requirements} onChange={(e) => set("visa_requirements", e.target.value)} data-testid="job-visa" /></Field>
          </div>
          <Field label="Country / regulatory notes"><textarea rows={2} className={inputCls} value={form.country_regulations} onChange={(e) => set("country_regulations", e.target.value)} data-testid="job-country" /></Field>
          <GdprConsent checked={form.gdpr_consent} onChange={(v) => set("gdpr_consent", v)} error={errors.gdpr_consent} />
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal disabled:opacity-40" data-testid="job-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="job-next">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-70" data-testid="job-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit Request
          </button>
        )}
      </div>
    </div>
  );
};

export default JobRequest;
